// ITAM-2583: pure resolution of the branch delivery fee from the branch SERVICE
// items. Extracted from React so it is unit-testable without a network/render.
//
// Root cause it fixes: /marketplace/{m}/{b} returns deliveryPrice/deliveryPriceFast
// = null for many branches, so the FE could not price a delivery order. The fee
// therefore comes from the branch SERVICE items, classified the way the mobile
// apps do, by price band and NOT by name. Names are unusable for selection: they
// were renamed in the DB (2026-09-22) to "Usluga rezervacije 1"/"2", whose
// suffixes do not say which is fast, and itemExternalName is no better:
//   - Android: standard is itemLowestPrice < 300, fast is itemLowestPrice > 300
//     (it then hardcodes 250/500; we do not, data-driven like iOS)
//   - iOS    : same split, then sends itemResponse.id and itemPrice ?? itemLowestPrice
// Exactly 300 sits on neither side of the split and is deliberately
// unclassifiable. iOS's cheapest/most-expensive fallback is deliberately NOT
// ported: an unclassifiable set must block, not guess.

import {
  BranchServiceItem,
  CncOrderDeliveryType,
} from "../types/consultOrder.types";

export type ServiceFeeStatus = "ok" | "missing" | "duplicate" | "malformed";

export interface ResolvedServiceFee {
  status: ServiceFeeStatus;
  serviceItemId: number | null;
  /** Usable fee (a genuine numeric 0 is valid) only when status === "ok"; else null. */
  price: number | null;
}

// Mobile classification boundary: STANDARD strictly below, FAST strictly above.
const FEE_BAND_BOUNDARY = 300;

const isUsableNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const classifiesAs = (
  item: BranchServiceItem,
  fulfillment: CncOrderDeliveryType
): boolean => {
  const lowest = item.itemLowestPrice;
  if (!isUsableNumber(lowest)) {
    return false;
  }
  return fulfillment === "STANDARD_DELIVERY"
    ? lowest < FEE_BAND_BOUNDARY
    : lowest > FEE_BAND_BOUNDARY;
};

/**
 * Resolve the delivery fee for a fulfillment from the branch SERVICE items.
 *   - missing   : no SERVICE item classifies into the fulfillment's price band
 *   - duplicate : more than one classifies (ambiguous, refuse to guess)
 *   - malformed : classified, but the id or price is unusable (null/NaN/negative)
 *   - ok        : a single candidate with a usable id + numeric price (0 allowed)
 * Only "ok" permits a delivery submission; every other status blocks it.
 */
export const resolveServiceFee = (
  items: BranchServiceItem[],
  fulfillment: CncOrderDeliveryType
): ResolvedServiceFee => {
  const matches = items.filter((item) => classifiesAs(item, fulfillment));

  if (matches.length === 0) {
    return { status: "missing", serviceItemId: null, price: null };
  }
  if (matches.length > 1) {
    return { status: "duplicate", serviceItemId: null, price: null };
  }

  const match = matches[0];
  const id = match.itemResponse?.id ?? null;
  // itemPrice takes precedence; ?? preserves a genuine 0 (only null/undefined fall through).
  const rawPrice = match.itemPrice ?? match.itemLowestPrice ?? null;
  const price = isUsableNumber(rawPrice) ? rawPrice : null;

  if (id == null || price == null || price < 0) {
    return { status: "malformed", serviceItemId: id, price: null };
  }
  return { status: "ok", serviceItemId: id, price };
};
